import {
  Box,
  CircularProgress,
  InputAdornment,
  List,
  TextField,
  Typography,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { lightGrey2, white } from '@styles/colors';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useGetMetrics } from '@features/enterpriseMetrics/hooks/useGetSavedMessages';
import { authContext } from '@staffedupusa/shared-components';

type OnboardingCompanyListProps = {
  selectedCompanyId?: number | null;
  setSelectedCompanyId: Dispatch<SetStateAction<number | null | undefined>>;
  setCompanyPlan: Dispatch<SetStateAction<number>>;
};

const OnboardingCompanyList: FC<OnboardingCompanyListProps> = ({
  selectedCompanyId,
  setCompanyPlan,
  setSelectedCompanyId
}) => {
  const { authData } = useContext(authContext);
  const { metricsData, isLoadingMetrics } = useGetMetrics({
    enterprise_id: authData?.user?.enterprise?.id,
    selectedCompanyId: 'ALL',
  });

  const [searchValue, setSearchValue] = useState('');

  const handleSelectedCompany = (companyId: number, planId: number) => {
    if (companyId) {
      setCompanyPlan(planId);
      setSelectedCompanyId(companyId);
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const filteredCompanies = metricsData?.companies?.filter(company =>
    company.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <Box bgcolor={white} borderRadius="8px" pt={3}>
      <Typography
        variant="h4"
        color="secondary"
        fontSize="24px"
        fontWeight="bold"
        px={3}
      >
        Companies:
      </Typography>
      <TextField
        variant="outlined"
        size="small"
        color="secondary"
        fullWidth
        placeholder="Search Company&hellip;"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="secondary" sx={{ fontSize: '20px' }} />
            </InputAdornment>
          ),
          endAdornment: searchValue ? (
            <InputAdornment
              position="end"
              onClick={() => setSearchValue('')}
              sx={{ cursor: 'pointer' }}
            >
              <ClearIcon color="secondary" sx={{ fontSize: '18px' }} />
            </InputAdornment>
          ) : null,
        }}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        sx={{
          '& input': {
            fontSize: '14px',
          },
          padding: '10px',
        }}
      />
      {isLoadingMetrics ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : filteredCompanies && filteredCompanies?.length > 0 ? (
        <List sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
          {filteredCompanies
            ?.sort((a, b) => a.name.localeCompare(b.name))
            ?.map((company) => (
              <ListItem
                key={company.company_id}
                sx={{
                  px: 3,
                  py: 0,
                  bgcolor: selectedCompanyId === company.company_id ? lightGrey2 : 'inherit',
                }}
              >
                <Tooltip arrow title={company.plan_status === 0 ? 'Company is Expired' : ''}>
                  <span style={{ display: 'block' }}>
                    <ListItemButton
                      onClick={() => handleSelectedCompany(company.company_id, company.plan_status)
                      }
                      disabled={company.plan_status === 0}
                    >
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '15px' }}
                        primary={company.name}
                      />
                    </ListItemButton>
                  </span>
                </Tooltip>
              </ListItem>
            ))}
        </List>
      ) : (
        <Typography sx={{ textAlign: 'center', margin: '10px' }}>
          No Company Found
        </Typography>
      )}
    </Box>
  );
};

export default OnboardingCompanyList;
